import { useRef, useState } from "react";
import { AlertTriangle, Loader2, Siren } from "lucide-react";

import { createClientRequestId } from "../../utils/clientRequestId";
import { riderApi } from "../services/riderApi";

const REASONS = [
  ["SAFETY_CONCERN", "Safety concern"],
  ["VEHICLE_ISSUE", "Vehicle issue"],
  ["NAVIGATION_ISSUE", "Navigation issue"],
  ["CUSTOMER_UNREACHABLE", "Customer unreachable"],
  ["ADDRESS_ISSUE", "Address issue"],
  ["PARCEL_ISSUE", "Parcel issue"],
  ["DEVICE_OR_NETWORK", "Device or network issue"],
  ["AUTHORITY_CONTESTED", "Controlling-device issue"],
  ["OTHER", "Another problem"],
];

function phaseFor(run, stop) {
  if (stop?.status === "ARRIVED") return "AFTER_ARRIVAL";
  if (stop?.status === "ACTIVE") return "EN_ROUTE";
  if (run.status === "READY") return "READY";
  return "OTHER";
}

export default function RiderExecutionProblemPanel({
  businessId, run, stop = null, executionHeaders = null, onReported,
}) {
  const [open, setOpen] = useState(false);
  const [reasonCode, setReasonCode] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const key = useRef(createClientRequestId(`execution_problem_${run.id}`));

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const result = await riderApi.reportExecutionProblem(businessId, run.id, {
        phase: phaseFor(run, stop),
        reasonCode,
        note,
        stopId: stop?.id || null,
        attemptId: stop?.attempt?.id || null,
        idempotencyKey: key.current,
      }, { executionHeaders });
      key.current = createClientRequestId(`execution_problem_${run.id}`);
      setOpen(false);
      setReasonCode("");
      setNote("");
      onReported?.(result);
    } catch (requestError) {
      setError(requestError?.message || "We couldn’t report this problem. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-3xl border border-amber-200 bg-amber-50 p-5 dark:border-amber-500/20 dark:bg-amber-500/10">
      <div className="flex items-start gap-3">
        <Siren className="mt-0.5 h-6 w-6 shrink-0 text-amber-700 dark:text-amber-300" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold">Tell the store about a problem</h2>
          <p className="mt-1 text-sm text-stone-700 dark:text-stone-300">A safety, vehicle, route, phone or parcel issue, at any point in the trip. It is saved with this trip and does not change your deliveries, and works even from a phone that is not controlling the trip. If you need help right away, call the store. If a delivery could not be made, use Delivery problem on that stop instead.</p>
        </div>
      </div>
      {!open ? <button type="button" onClick={() => setOpen(true)} className="mt-4 min-h-12 rounded-2xl bg-amber-700 px-5 font-semibold text-white">Report a problem to the store</button> : (
        <div className="mt-4 space-y-3">
          {error && <p role="alert" className="flex gap-2 rounded-xl bg-red-50 p-3 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-200"><AlertTriangle className="h-5 w-5 shrink-0" />{error}</p>}
          <label className="block text-sm font-semibold">What needs attention?<select value={reasonCode} onChange={(event) => setReasonCode(event.target.value)} className="mt-2 min-h-12 w-full rounded-2xl border border-amber-300 bg-white px-4 font-normal text-stone-950 dark:border-amber-500/30 dark:bg-neutral-900 dark:text-white"><option value="">Choose a problem</option>{REASONS.map(([value, text]) => <option key={value} value={value}>{text}</option>)}</select></label>
          <label className="block text-sm font-semibold">Operational note {reasonCode !== "OTHER" && <span className="font-normal text-stone-500">(optional)</span>}<textarea value={note} onChange={(event) => setNote(event.target.value)} maxLength={1000} rows={3} className="mt-2 w-full rounded-2xl border border-amber-300 bg-white p-4 font-normal text-stone-950 dark:border-amber-500/30 dark:bg-neutral-900 dark:text-white" /></label>
          <div className="grid grid-cols-2 gap-3"><button type="button" disabled={busy} onClick={() => setOpen(false)} className="min-h-12 rounded-2xl border border-amber-300 font-semibold">Cancel</button><button type="button" disabled={busy || !reasonCode || (reasonCode === "OTHER" && note.trim().length < 10)} onClick={submit} className="flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-amber-700 font-semibold text-white disabled:opacity-50">{busy && <Loader2 className="h-5 w-5 animate-spin" />} Send report</button></div>
        </div>
      )}
    </section>
  );
}
